import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { leaderboardService } from '@services';
import { useAuthStore } from '@app/stores/authStore';

export const LEADERBOARD_REWARDS_QUERY_KEY = [
  'leaderboard',
  'pending-rewards',
] as const;

export const useLeaderboardRewards = () => {
  const isAuthenticated = useAuthStore((state) => !!state.accessToken);

  const {
    data: pendingRewards = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: LEADERBOARD_REWARDS_QUERY_KEY,
    queryFn: () => leaderboardService.getPendingRewards(),
    enabled: isAuthenticated,
    staleTime: 1000 * 60 * 2,
    gcTime: 1000 * 60 * 5,
  });

  return {
    pendingRewards,
    hasPendingRewards: pendingRewards.length > 0,
    isLoading,
    error,
    refetch,
  };
};

export const useAcknowledgeReward = () => {
  const queryClient = useQueryClient();
  const validateAndUpdateUser = useAuthStore(
    (state) => state.validateAndUpdateUser
  );

  const acknowledgeMutation = useMutation({
    mutationFn: (rewardId: string) =>
      leaderboardService.acknowledgeReward(rewardId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: LEADERBOARD_REWARDS_QUERY_KEY,
      });
      // Refresh user profile to get updated gold/energy
      await validateAndUpdateUser();
    },
  });

  const acknowledgeAllMutation = useMutation({
    mutationFn: () => leaderboardService.acknowledgeAllRewards(),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: LEADERBOARD_REWARDS_QUERY_KEY,
      });
      await validateAndUpdateUser();
    },
  });

  return {
    acknowledge: acknowledgeMutation.mutateAsync,
    acknowledgeAll: acknowledgeAllMutation.mutateAsync,
    isAcknowledging:
      acknowledgeMutation.isPending || acknowledgeAllMutation.isPending,
  };
};
